// Persistent game state. Everything the player earns or unlocks lives here and
// survives page reloads via localStorage. On breadtoasting.com this is the real
// save file; swap the backend for a server API later without touching callers.

const KEY = 'toasttown.save.v1';

const DEFAULT_STATE = {
  crumbs: 5,
  lumber: 0,               // resource chopped in the Forest; feeds town-building later
  // pet needs (0-100)
  pet: { hunger: 80, fun: 80, clean: 80, energy: 80 },
  // player energy (0-100); actions cost energy, sleeping restores it
  energy: 100,
  // wardrobe: array of owned cosmetic ids; equipped keyed by slot
  wardrobe: { owned: [], equipped: { hat: null, tie: null, shirt: null } },
  // recipes unlocked for the café/kitchen minigame
  recipes: ['pb', 'jelly', 'honey'],
  // per-building upgrade levels, keyed by building id
  buildings: {
    house:  { level: 1 },
    comics: { level: 1 },   // open: Gary's Comics sorting minigame
    cafe:   { level: 1 },   // open: the Yogurt Café rhythm game is playable
    court:  { level: 1 },   // open: Avocado Court basketball + rec center
    dojo:   { level: 1 },
    casino: { level: 1 },
    apartment: { level: 1 },
    garage: { level: 1 },
    grill: { level: 1 },
    beach: { level: 1 },
    aquarium: { level: 1 },
    bananaVillage: { level: 1 }
  },
  // quests: the RPG spine. `active` maps quest id -> live progress ({count, flags});
  // `completed` is the list of turned-in quest ids. See core/quests.js.
  quests: { active: {}, completed: [] },
  // collection: found items (fossils/artifacts/junk) keyed by id -> count. Feeds
  // the Museum. Populated by dig spots. See core/collection.js.
  collection: { found: {} },
  // generic world flags set by quests/events (e.g. fortressOpen) — cheap booleans
  // that scenes read to unlock content without bespoke save fields each time.
  flags: {},
  // world clock: hour (0–24) and day counter. Drives day/night, shop hours,
  // and the "each task takes time" loop.
  world: { hour: 8, day: 1 },
  // trainable character stats (1-10). str/agi/int/zen power gym, dojo, combat, space.
  stats: { str: 1, agi: 1, int: 1, zen: 1 },
  // apartment: furniture placed in the player's uptown apartment
  apartment: { furniture: [], theme: 'default' },
  // garage: owned cars and the one currently equipped
  garage: { owned: [], equipped: null },
  // farm: grid of plots + inventory of seeds and harvested crops
  farm: {
    width: 3, height: 3,          // unlocked plot grid
    plots: []                     // { crop, plantedHour, plantedDay }
  },
  inventory: { seeds: { wheat: 3 }, crops: {}, ingredients: {}, dishes: {} },
  // temporary dish buffs
  buffs: [],
  // park: which flower/tree spots are planted (keyed by spot index)
  park: { flowers: {}, trees: {} },
  // speedway: race records (additive — new for the Speedway item)
  speedway: { bestLapMs: null, wins: 0, raced: 0 },
  // bank: deposited savings balance + last day interest was claimed
  bank: { balance: 0, lastInterestDay: 1 },
  // town meta
  visited: ['town'],
  lastSaved: 0
};

// Buildings that have shipped as playable. Older saves may have persisted these
// at level 0 (locked) before they launched — migrate them open on load so they
// don't stay stuck on "coming soon" for returning players.
const OPEN_BUILDINGS = ['house', 'cafe', 'court', 'comics', 'grill', 'beach', 'aquarium', 'bananaVillage'];

let state = load();

function load(){
  let s;
  try {
    const raw = localStorage.getItem(KEY);
    // deep-merge saved over defaults so new fields in future versions get defaults
    s = raw ? deepMerge(structuredClone(DEFAULT_STATE), JSON.parse(raw)) : structuredClone(DEFAULT_STATE);
  } catch (e) {
    console.warn('save load failed, starting fresh', e);
    s = structuredClone(DEFAULT_STATE);
  }
  for (const id of OPEN_BUILDINGS){
    if (s.buildings[id] && s.buildings[id].level < 1) s.buildings[id].level = 1;
  }
  return s;
}

function deepMerge(base, over){
  for (const k in over){
    if (over[k] && typeof over[k] === 'object' && !Array.isArray(over[k])){
      base[k] = deepMerge(base[k] ?? {}, over[k]);
    } else {
      base[k] = over[k];
    }
  }
  // migration: old saves without new fields get defaults
  if (!base.world || typeof base.world.hour !== 'number') base.world = { hour: 8, day: 1 };
  if (!base.farm) base.farm = { width: 3, height: 3, plots: [] };
  if (!base.inventory) base.inventory = { seeds: {}, crops: {}, ingredients: {}, dishes: {} };
  else {
    if (!base.inventory.ingredients) base.inventory.ingredients = {};
    if (!base.inventory.dishes) base.inventory.dishes = {};
  }
  if (!base.buffs) base.buffs = [];
  if (!base.park) base.park = { flowers: {}, trees: {} };
  if (!base.speedway) base.speedway = { bestLapMs: null, wins: 0, raced: 0 };
  if (!base.stats || typeof base.stats.str !== 'number') base.stats = { str: 1, agi: 1, int: 1, zen: 1 };
  if (typeof base.energy !== 'number') base.energy = 100;
  if (!base.bank || typeof base.bank.balance !== 'number') base.bank = { balance: 0, lastInterestDay: 1 };
  return base;
}

let saveTimer = null;
export function save(){
  state.lastSaved = Date.now();
  // debounce rapid writes (e.g. during a minigame payout burst)
  clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    try { localStorage.setItem(KEY, JSON.stringify(state)); }
    catch (e) { console.warn('save write failed', e); }
  }, 250);
}

export function getState(){ return state; }

export function resetSave(){
  state = structuredClone(DEFAULT_STATE);
  save();
}

// ---- convenience accessors ----
export function getCrumbs(){ return state.crumbs; }
export function addCrumbs(n){ state.crumbs = Math.max(0, state.crumbs + n); save(); return state.crumbs; }
export function spendCrumbs(n){
  if (state.crumbs < n) return false;
  state.crumbs -= n; save(); return true;
}

// ---- bank ----
export function getBank(){ return state.bank; }
export function getBankBalance(){ return state.bank.balance; }
export function depositBank(n){
  if (state.crumbs < n) return false;
  state.crumbs -= n; state.bank.balance += n; save(); return true;
}
export function withdrawBank(n){
  if (state.bank.balance < n) return false;
  state.bank.balance -= n; state.crumbs += n; save(); return true;
}
export function bankInterestAvailable(day){
  return day > state.bank.lastInterestDay && state.bank.balance > 0;
}
export function peekBankInterest(day, rate = 0.05){
  if (!bankInterestAvailable(day)) return 0;
  return Math.max(1, Math.floor(state.bank.balance * rate));
}
export function claimBankInterest(day, rate = 0.05){
  if (!bankInterestAvailable(day)) return 0;
  const interest = Math.max(1, Math.floor(state.bank.balance * rate));
  state.bank.balance += interest;
  state.bank.lastInterestDay = day;
  save();
  return interest;
}

export function getLumber(){ return state.lumber; }
export function addLumber(n){ state.lumber = Math.max(0, state.lumber + n); save(); return state.lumber; }
export function spendLumber(n){
  if (state.lumber < n) return false;
  state.lumber -= n; save(); return true;
}

// ---- world flags (quest/event unlocks) ----
export function getFlag(id){ return !!state.flags[id]; }
export function setFlag(id, v = true){ state.flags[id] = v; save(); }

// ---- quests (see core/quests.js for the engine + registry) ----
export function getQuests(){ return state.quests; }

// ---- collection (see core/collection.js for the item registry) ----
export function getCollection(){ return state.collection; }

export function getBuilding(id){ return state.buildings[id]; }
export function upgradeBuilding(id){ state.buildings[id].level++; save(); }

// ---- wardrobe (cosmetics bought at Toast Threads) ----
export function getWardrobe(){ return state.wardrobe; }
export function ownsCosmetic(id){ return state.wardrobe.owned.includes(id); }
export function getEquipped(slot){ return state.wardrobe.equipped[slot] || null; }

// Buy a cosmetic: no-op success if already owned, else spend crumbs and add it
// to owned. Returns true on success, false if the player can't afford it.
export function buyCosmetic(id, price){
  if (ownsCosmetic(id)) return true;
  if (!spendCrumbs(price)) return false;
  state.wardrobe.owned.push(id);
  save();
  return true;
}

export function equipCosmetic(slot, id){ state.wardrobe.equipped[slot] = id; save(); }
export function unequipSlot(slot){ state.wardrobe.equipped[slot] = null; save(); }

// ---- inventory (seeds + harvested crops) -------------------------------
export function getInventory(){ return state.inventory; }
export function addSeeds(id, n = 1){ state.inventory.seeds[id] = (state.inventory.seeds[id] || 0) + n; save(); }
export function spendSeeds(id, n = 1){
  if ((state.inventory.seeds[id] || 0) < n) return false;
  state.inventory.seeds[id] -= n; save(); return true;
}
export function addCrops(id, n = 1){ state.inventory.crops[id] = (state.inventory.crops[id] || 0) + n; save(); }
export function spendCrops(id, n = 1){
  if ((state.inventory.crops[id] || 0) < n) return false;
  state.inventory.crops[id] -= n; save(); return true;
}

// ---- farm plots --------------------------------------------------------
export function getFarm(){ return state.farm; }
export function expandFarm(dir){
  const oldW = state.farm.width, oldH = state.farm.height;
  const oldPlots = state.farm.plots;
  if (dir === 'width'){
    const newPlots = [];
    for (let z = 0; z < oldH; z++){
      for (let x = 0; x < oldW; x++) newPlots.push(oldPlots[z * oldW + x] || null);
      newPlots.push(null); // new column
    }
    state.farm.plots = newPlots;
    state.farm.width++;
  } else if (dir === 'height'){
    state.farm.plots = oldPlots.slice();
    for (let x = 0; x < oldW; x++) state.farm.plots.push(null);
    state.farm.height++;
  }
  save();
}
export function plantPlot(index, crop, hour, day){
  state.farm.plots[index] = { crop, plantedHour: hour, plantedDay: day, watered: 0 };
  save();
}
export function harvestPlot(index){
  const p = state.farm.plots[index];
  if (!p) return null;
  state.farm.plots[index] = null;
  addCrops(p.crop, 1);
  save();
  return p.crop;
}

export function waterPlot(index){
  const p = state.farm.plots[index];
  if (!p) return false;
  p.watered = (p.watered || 0) + 1;
  save();
  return true;
}

// ---- park planting ---------------------------------------------------------
export function getPark(){ return state.park; }
export function hasFlower(idx){ return !!state.park.flowers[idx]; }
export function hasTree(idx){ return !!state.park.trees[idx]; }
export function plantFlower(idx){
  state.park.flowers[idx] = true;
  save();
}
export function plantTree(idx){
  state.park.trees[idx] = true;
  save();
}

// ---- speedway records -----------------------------------------------------
export function getSpeedway(){ return state.speedway; }
export function recordRace(placement, lapMs){
  state.speedway.raced++;
  if (placement === 1) state.speedway.wins++;
  if (lapMs && (state.speedway.bestLapMs == null || lapMs < state.speedway.bestLapMs)){
    state.speedway.bestLapMs = lapMs;
  }
  save();
}

// ---- apartment furniture --------------------------------------------------
export function getApartment(){ return state.apartment; }
export function addFurniture(id){
  if (!state.apartment.furniture.includes(id)) state.apartment.furniture.push(id);
  save();
}
export function hasFurniture(id){ return state.apartment.furniture.includes(id); }
export function setApartmentTheme(theme){ state.apartment.theme = theme; save(); }

// ---- stats ----------------------------------------------------------------
const MAX_STAT = 10;
export function getStats(){ return state.stats; }

function pruneBuffs(){
  const now = Date.now();
  if (!state.buffs) state.buffs = [];
  const before = state.buffs.length;
  state.buffs = state.buffs.filter(b => b.expires > now);
  if (state.buffs.length !== before) save();
}

export function getStat(key){
  pruneBuffs();
  const base = state.stats[key] ?? 1;
  const bonus = (state.buffs || []).reduce((sum, b) => b.stat === key ? sum + b.amount : sum, 0);
  return Math.min(MAX_STAT, Math.max(1, base + bonus));
}
export function addStat(key, n = 1){
  const before = state.stats[key] ?? 1;
  state.stats[key] = Math.min(MAX_STAT, Math.max(1, before + n));
  save();
  return state.stats[key];
}
// Train a stat: gain `n` (can be fractional), return whether it increased.
export function trainStat(key, n = 1){
  const before = state.stats[key] ?? 1;
  state.stats[key] = Math.min(MAX_STAT, before + n);
  const gained = Math.floor(state.stats[key]) - Math.floor(before);
  save();
  return gained;
}

// ---- garage / cars --------------------------------------------------------
export function getGarage(){ return state.garage; }
export function ownsCar(id){ return state.garage.owned.includes(id); }
export function buyCar(id, price){
  if (ownsCar(id)) return true;
  if (!spendCrumbs(price)) return false;
  state.garage.owned.push(id);
  if (!state.garage.equipped) state.garage.equipped = id;
  save();
  return true;
}
export function equipCar(id){
  if (!ownsCar(id)) return false;
  state.garage.equipped = id;
  save();
  return true;
}
export function getEquippedCar(){ return state.garage?.equipped || null; }

// ---- energy / action budget ----------------------------------------------
export function getEnergy(){ return state.energy; }
export function setEnergy(n){
  state.energy = Math.max(0, Math.min(100, n));
  save();
}
export function restoreEnergy(n){ setEnergy(state.energy + n); }
export function spendEnergy(n){ setEnergy(state.energy - n); }
